import { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getPagedDocuments,
  getRelatedData,
  getDocumentForEdit,
  createDocument,
  updateDocument,
  convertDocumentToHtml,
  deleteDocument,
} from '../../../../services/documentConfigService';
import DocumentCreateEditModal from './DocumentCreateEditModal';
import DocumentViewModal from './DocumentViewModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { documentDeliveryI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';

export default function DocumentDelivery() {
  const { language } = useLanguage();
  const documentsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: documentDeliveryI18n });
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, IconButton, Modal, Dropdown, ComboBox } =
    DesignSystemComponents ?? {};

  const {
    canCreateDocument,
    canViewDocument,
    canEditDocument,
    canDeleteDocument,
  } = usePermissionsConfiguration();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);
  const [writeGroupOptions, setWriteGroupOptions] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showView, setShowView] = useState(false);
  const [viewTemplate, setViewTemplate] = useState(null);
  const stateSearch = location.state?.search ?? '';

  useEffect(() => {
    documentDeliveryI18n.changeLanguage(documentsLanguage);
  }, [documentsLanguage]);

  useEffect(() => {
    if (stateSearch) {
      setSearch(stateSearch);
      setPage(0);
    }
  }, [stateSearch]);

  const variableGroups = useMemo(
    () => [
      {
        label: t('doc_delivery_variable_group_legal365'),
        variables: [
          {
            label: t('doc_delivery_variable_client_name'),
            placeholder: '@CLIENTE_NOMBRE',
          },
          {
            label: t('doc_delivery_variable_client_rut'),
            placeholder: '@CLIENTE_RUT',
          },
          {
            label: t('doc_delivery_variable_client_email'),
            placeholder: '@CLIENTE_EMAIL',
          },
          {
            label: t('doc_delivery_variable_case_number'),
            placeholder: '@CASO_NUMERO',
          },
          {
            label: t('doc_delivery_variable_case_type'),
            placeholder: '@CASO_TIPO',
          },
        ],
      },
      {
        label: t('doc_delivery_variable_group_registry'),
        variables: [
          {
            label: t('doc_delivery_variable_civil_status'),
            placeholder: '@ESTADO_CIVIL',
          },
          { label: t('doc_delivery_variable_profession'), placeholder: '@PROFESION' },
        ],
      },
      {
        label: t('doc_delivery_variable_group_pjud'),
        variables: [
          { label: t('doc_delivery_variable_pjud_role'), placeholder: '@PJUD_ROL' },
          { label: t('doc_delivery_variable_pjud_case'), placeholder: '@PJUD_CAUSA' },
          {
            label: t('doc_delivery_variable_pjud_court'),
            placeholder: '@PJUD_TRIBUNAL',
          },
        ],
      },
    ],
    [t]
  );

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'serviceName':
        return 'serviceName';
      case 'businessAreaName':
        return 'businessAreaName';
      case 'updatedAt':
        return 'updatedAt';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'updatedAt';
  const backendSortDesc =
    sortDirection === null ? true : sortDirection === 'desc';
  const currentLocale = useMemo(() => {
    if (documentsLanguage === 'enUS') return 'en-US';
    if (documentsLanguage === 'ptBR') return 'pt-BR';
    return 'es-CL';
  }, [documentsLanguage]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await getPagedDocuments(
        page + 1,
        pageSize,
        search,
        true,
        backendSortKey,
        backendSortDesc
      );
      setRows(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading documents:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_load_document_data'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [page, search, pageSize, backendSortKey, backendSortDesc]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    setTableKey((prev) => prev + 1);
  };

  const onCreate = async () => {
    setEditTemplate(null);

    try {
      const relatedData = await getRelatedData();

      const mappedServices = (relatedData.services || [])
        .filter((service) => service.isEnabled)
        .map((service) => ({
          label: service.name,
          value: service.id,
        }));

      const mappedAreas = (relatedData.businessAreas || [])
        .filter((area) => area.isEnabled)
        .map((area) => ({
          label: area.name,
          value: area.id,
        }));

      setServiceOptions(mappedServices);
      setAreaOptions(mappedAreas);
      setDeliveryTypeOptions(relatedData.deliveryType || []);
      setWriteGroupOptions(relatedData.documentGroups || []);

      setShowCreateTemplate(true);
    } catch (error) {
      console.error('Error loading related data:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_related_data'), 'error');
    }
  };

  const onView = async (row) => {
    try {
      const fullDocument = await getDocumentForEdit(row.id);
      setViewTemplate(fullDocument);
      setShowView(true);
    } catch (error) {
      console.error('Error loading document:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_load_document'), 'error');
    }
  };

  const onEdit = async (row) => {
    try {
      const documentData = await getDocumentForEdit(row.id);
      const relatedData = await getRelatedData();

      const mappedServices = (relatedData.services || [])
        .filter((service) => service.isEnabled)
        .map((service) => ({
          label: service.name,
          value: service.id,
        }));

      const mappedAreas = (relatedData.businessAreas || [])
        .filter((area) => area.isEnabled)
        .map((area) => ({
          label: area.name,
          value: area.id,
        }));

      setServiceOptions(mappedServices);
      setAreaOptions(mappedAreas);
      setDeliveryTypeOptions(relatedData.deliveryType || []);
      setWriteGroupOptions(relatedData.documentGroups || []);
      setEditTemplate(documentData);
      setShowCreateTemplate(true);
    } catch (error) {
      console.error('Error loading document data:', error);
      showSnackbar(
        error.customMessage || t('doc_delivery_error_load_document_data'),
        'error'
      );
    }
  };

  const onDelete = (row) => {
    setSelectedTemplate(row);
    setShowDelete(true);
  };

  const handleCreateTemplate = async (payload) => {
    try {
      if (payload.mode === 'edit') {
        await updateDocument(payload.id, {
          name: payload.name,
          serviceId: payload.serviceId,
          businessAreaId: payload.areaId,
          deliveryTypeId: payload.deliveryTypeId,
          documentGroupId: payload.writeGroupId || null,
          documentTypeId: payload.writeTypeId || null,
          content: payload.content,
        });
      } else {
        await createDocument({
          name: payload.name,
          serviceId: payload.serviceId,
          businessAreaId: payload.areaId,
          deliveryTypeId: payload.deliveryTypeId,
          documentGroupId: payload.writeGroupId || null,
          documentTypeId: payload.writeTypeId || null,
          content: payload.content,
        });
      }

      await fetchDocuments();
      setShowCreateTemplate(false);
      setEditTemplate(null);
      showSnackbar(t('doc_delivery_success_saved'), 'success');
    } catch (error) {
      console.error('Error saving document:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_save'), 'error');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteDocument(selectedTemplate.id);

      await fetchDocuments();
      setShowDelete(false);
      setSelectedTemplate(null);
      showSnackbar(t('doc_delivery_success_deleted'), 'success');
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_delete'), 'error');
    }
  };

  const handleConvertTemplateFile = async (file) => {
    try {
      const html = await convertDocumentToHtml(file);
      return html;
    } catch (error) {
      console.error('Error converting file:', error);
      showSnackbar(error.customMessage || t('doc_delivery_error_convert'), 'error');
      throw error;
    }
  };

  if (!Card || !Button || !Input || !Table) return null;

  return (
    <Card specialPadding='p-4'>
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <h2 className='mb-1'>{t('doc_delivery_title')}</h2>
          <p className='caption font-light'>{t('doc_delivery_subtitle')}</p>
        </div>
        <Button
          label={t('doc_delivery_create_button')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={onCreate}
          disabled={!canCreateDocument}
        />
      </div>
      <div className='mb-3'>
        <Input
          icon='search'
          name='searchDocuments'
          placeholder={t('doc_delivery_search_placeholder')}
          type='text'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
      </div>
      <div className='table-container'>
        <Table
          key={`table-${tableKey}-${pageSize}`}
          columns={[
            { accessor: 'name', label: t('doc_delivery_col_document'), sort: true },
            { accessor: 'serviceName', label: t('doc_delivery_col_service'), sort: true },
            { accessor: 'businessAreaName', label: t('doc_delivery_col_area'), sort: true },
            {
              accessor: 'updatedAt',
              label: t('doc_delivery_col_updated_at'),
              sort: true,
              render: (row) =>
                new Date(row.updatedAt).toLocaleDateString(currentLocale),
            },
            {
              label: t('doc_delivery_col_actions'),
              render: (row) => (
                <div className='d-flex gap-3'>
                  <IconButton
                    ariaLabel={t('doc_delivery_action_view')}
                    icon='eye'
                    onClick={() => onView(row)}
                    disabled={!canViewDocument}
                  />
                  <IconButton
                    ariaLabel={t('doc_delivery_action_edit', { name: row.name })}
                    icon='pencil'
                    onClick={() => onEdit(row)}
                    disabled={!canEditDocument}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('doc_delivery_action_delete', { name: row.name })}
                    icon='trash'
                    onClick={() => onDelete(row)}
                    disabled={!canDeleteDocument}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          paginator
          paginationMode='server'
          totalItems={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          page={page}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
          loading={loading}
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
        />
      </div>

      <DocumentCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        ComboBox={ComboBox}
        Button={Button}
        show={showCreateTemplate}
        mode={editTemplate ? 'edit' : 'create'}
        initialData={editTemplate}
        onClose={() => {
          setShowCreateTemplate(false);
          setEditTemplate(null);
        }}
        onSave={handleCreateTemplate}
        serviceOptions={serviceOptions}
        areaOptions={areaOptions}
        deliveryTypeOptions={deliveryTypeOptions}
        writeGroupOptions={writeGroupOptions}
        variableGroups={variableGroups}
        uploadFileToHtml={handleConvertTemplateFile}
        tinyApiKey={process.env.REACT_APP_TINYMCE_KEY}
      />

      <ConfirmModal
        Modal={Modal}
        show={showDelete}
        title={t('doc_delivery_confirm_delete_title')}
        message={
          selectedTemplate
            ? t('doc_delivery_confirm_delete_message', {
                name: selectedTemplate.name,
              })
            : t('doc_delivery_confirm_delete_fallback')
        }
        confirmLabel={t('doc_delivery_confirm_delete_button')}
        confirmVariant='danger'
        onConfirm={handleDelete}
        onClose={() => setShowDelete(false)}
      />

      <DocumentViewModal
        Modal={Modal}
        show={showView}
        record={viewTemplate}
        onClose={() => {
          setShowView(false);
          setViewTemplate(null);
        }}
      />
    </Card>
  );
}
